import React, { useEffect, useState } from "react";
import { Button, Empty, Select, Spin, Typography } from "antd";
import { ArrowUpOutlined, HolderOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import { arrayMoveImmutable } from "array-move";
import CustomTable from "components/CustomTable";
import StateSwitch from "components/StateSwitch";
import {
  ANNOUNCEMENT_BAR_POSITION,
  ANNOUNCEMENT_BAR_POSITION_LABELS,
  BANNER_SCREEN_LABELS,
  BANNER_SCREENS,
  BANNER_SOURCE_LABELS,
  BANNER_TYPE,
  BANNER_TYPE_LABELS,
  POPUP_TRIGGER_TYPE,
  POPUP_TRIGGER_TYPE_LABELS,
} from "../../../core/constants";
import { formatNavigationSummary } from "../../../core/utils";
import { useBannerQueue, useSaveBannerQueue } from "../../../data/hooks";
import "../../styles/marketingBanner.scss";

const { Option } = Select;
const { Text } = Typography;

// Sortable DnD components
const DragHandle = SortableHandle(() => <HolderOutlined className="drag-handle" title="Kéo để đổi thứ tự" />);

const SortableItem = SortableElement((props) => <tr {...props} />);
const SortableBody = SortableContainer((props) => <tbody {...props} />);

function BannerQueuePage() {
  const history = useHistory();

  // Queue key state
  const [selectedType, setSelectedType] = useState(BANNER_TYPE.BANNER);
  const [selectedScreen, setSelectedScreen] = useState(BANNER_SCREENS.HOME);
  const [selectedTriggerType, setSelectedTriggerType] = useState(POPUP_TRIGGER_TYPE.APP_OPEN_OR_SCREEN_ENTER);
  const [selectedPosition, setSelectedPosition] = useState(ANNOUNCEMENT_BAR_POSITION.TOP);

  // Build key input for backend
  const queueKeyInput = {
    type: selectedType,
    screen: selectedScreen,
    triggerType: selectedType === BANNER_TYPE.POPUP ? selectedTriggerType : null,
    position: selectedType === BANNER_TYPE.ANNOUNCEMENT_BAR ? selectedPosition : null,
  };

  const { queueItems, loading: loadingQueue, refetch } = useBannerQueue(queueKeyInput);
  const { saveQueue, loading: savingQueue } = useSaveBannerQueue();

  // Local reorderable list: array of { banner, queuePosition, isActive }
  const [items, setItems] = useState([]);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (queueItems) {
      setItems(queueItems);
      setIsDirty(false);
    }
  }, [queueItems]);

  // Handle Drag & Drop sort end
  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex === newIndex) return;
    const reordered = arrayMoveImmutable(items, oldIndex, newIndex);
    setItems(reordered);
    setIsDirty(true);
  };

  // Move item to top ("Ưu tiên cao nhất")
  const handleMoveToTop = (index) => {
    if (index === 0) return;
    const reordered = arrayMoveImmutable(items, index, 0);
    setItems(reordered);
    setIsDirty(true);
  };

  // Toggle active on this screen
  const handleToggleScreenActive = (index, newActive) => {
    const updated = [...items];
    updated[index] = {
      ...updated[index],
      isActive: newActive,
    };
    setItems(updated);
    setIsDirty(true);
  };

  // Save queue to backend
  const handleSave = async () => {
    try {
      const payload = {
        key: queueKeyInput,
        items: items.map((item) => ({
          bannerID: item.banner.id,
          isActive: Boolean(item.isActive),
        })),
      };
      await saveQueue(payload);
      setIsDirty(false);
    } catch (err) {
      // Nếu hàng chờ bị đổi bởi thao tác khác, tải lại dữ liệu mới nhất
      refetch();
    }
  };

  const DraggableBody = (props) => (
    <SortableBody useDragHandle helperClass="row-dragging" onSortEnd={onSortEnd} {...props} />
  );

  const DraggableRow = ({ className, style, ...restProps }) => {
    const rowKey = restProps["data-row-key"];
    const index = items.findIndex((it) => it.banner?.id === rowKey);
    return <SortableItem index={index >= 0 ? index : 0} {...restProps} />;
  };

  const columns = [
    {
      title: "",
      key: "dragHandle",
      width: 44,
      align: "center",
      render: () => <DragHandle />,
    },
    {
      title: "STT",
      key: "stt",
      width: 60,
      align: "center",
      render: (_, record, index) => {
        // Items with isActive: true get position index + 1; inactive gets null/—
        return record.isActive ? (
          <strong style={{ color: "#2246dc" }}>{index + 1}</strong>
        ) : (
          <span style={{ color: "#999999" }}>—</span>
        );
      },
    },
    {
      title: "TÊN BANNER",
      key: "name",
      render: (_, record) => (
        <span
          onClick={() => history.push(`/marketing-banner/detail/${record.banner.id}`)}
          style={{ fontWeight: 600, color: "#2246dc", cursor: "pointer" }}
        >
          {record.banner.name}
        </span>
      ),
    },
    {
      title: "LINK ĐIỀU HƯỚNG",
      key: "navigation",
      render: (_, record) => formatNavigationSummary(record.banner.navigation),
    },
    {
      title: "NGUỒN BANNER",
      key: "source",
      width: 160,
      render: (_, record) => BANNER_SOURCE_LABELS[record.banner.source] || record.banner.source,
    },
    {
      title: "HÌNH ẢNH",
      key: "image",
      width: 140,
      align: "center",
      render: (_, record) => {
        const img = record.banner.imageURL || record.banner.squareImageURL || record.banner.announcementBar?.iconURL;
        return img ? (
          <img
            src={img}
            alt="thumb"
            style={{
              width: "80px",
              height: "40px",
              objectFit: "cover",
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          />
        ) : (
          <span style={{ color: "#bfbfbf", fontSize: "12px" }}>Không có ảnh</span>
        );
      },
    },
    {
      title: "TRẠNG THÁI",
      key: "isActive",
      width: 120,
      align: "center",
      render: (_, record, index) => (
        <StateSwitch
          size="small"
          checked={record.isActive}
          onChange={(checked) => handleToggleScreenActive(index, checked)}
        />
      ),
    },
    {
      title: "THAO TÁC",
      key: "action",
      width: 160,
      align: "center",
      render: (_, record, index) => (
        <Button
          size="small"
          icon={<ArrowUpOutlined />}
          onClick={() => handleMoveToTop(index)}
          disabled={index === 0}
          style={{
            borderColor: "#2246dc",
            color: index === 0 ? "#bfbfbf" : "#2246dc",
            borderRadius: "4px",
            fontSize: "12px",
          }}
        >
          Ưu tiên cao nhất
        </Button>
      ),
    },
  ];

  return (
    <div className="marketing-banner-container">
      {/* Header */}
      <div className="banner-page-header">
        <div className="banner-breadcrumb">
          <span style={{ cursor: "pointer", color: "#2246dc" }} onClick={() => history.push("/marketing-banner")}>
            Quản lý công cụ truyền thông
          </span>{" "}
          / Cấu hình hàng chờ
        </div>
        <h1 className="banner-page-title">Cấu hình vị trí / trạng thái</h1>
      </div>

      {/* Selectors Bar */}
      <div className="banner-toolbar-card">
        <div style={{ display: "flex", gap: "16px", alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <Text type="secondary" style={{ display: "block", fontSize: "12px", marginBottom: 4 }}>
              Loại công cụ truyền thông
            </Text>
            <Select value={selectedType} onChange={(val) => setSelectedType(val)} style={{ width: "200px" }}>
              {Object.entries(BANNER_TYPE_LABELS).map(([k, v]) => (
                <Option key={k} value={k}>
                  {v}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <Text type="secondary" style={{ display: "block", fontSize: "12px", marginBottom: 4 }}>
              Vị trí màn hình hiển thị
            </Text>
            <Select value={selectedScreen} onChange={(val) => setSelectedScreen(val)} style={{ width: "220px" }}>
              {Object.entries(BANNER_SCREEN_LABELS).map(([k, v]) => (
                <Option key={k} value={k}>
                  {v}
                </Option>
              ))}
            </Select>
          </div>

          {selectedType === BANNER_TYPE.POPUP && (
            <div>
              <Text type="secondary" style={{ display: "block", fontSize: "12px", marginBottom: 4 }}>
                Thời điểm kích hoạt
              </Text>
              <Select
                value={selectedTriggerType}
                onChange={(val) => setSelectedTriggerType(val)}
                style={{ width: "300px" }}
              >
                {Object.entries(POPUP_TRIGGER_TYPE_LABELS).map(([k, v]) => (
                  <Option key={k} value={k}>
                    {v}
                  </Option>
                ))}
              </Select>
            </div>
          )}

          {selectedType === BANNER_TYPE.ANNOUNCEMENT_BAR && (
            <div>
              <Text type="secondary" style={{ display: "block", fontSize: "12px", marginBottom: 4 }}>
                Vị trí hiển thị
              </Text>
              <Select value={selectedPosition} onChange={(val) => setSelectedPosition(val)} style={{ width: "220px" }}>
                {Object.entries(ANNOUNCEMENT_BAR_POSITION_LABELS).map(([k, v]) => (
                  <Option key={k} value={k}>
                    {v}
                  </Option>
                ))}
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Reorderable Table */}
      <div className="banner-table-card">
        {loadingQueue ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : items.length === 0 ? (
          <Empty description="Không có banner nào trong hàng chờ của màn hình này." />
        ) : (
          <CustomTable
            rowKey={(record) => record.banner.id}
            dataSource={items}
            columns={columns}
            pagination={false}
            components={{
              body: {
                wrapper: DraggableBody,
                row: DraggableRow,
              },
            }}
          />
        )}
      </div>

      {/* Bottom Actions */}
      <div className="banner-actions-footer">
        <Button className="btn-cancel" onClick={() => history.push("/marketing-banner")}>
          Hủy bỏ
        </Button>
        <Button type="primary" className="btn-submit" onClick={handleSave} loading={savingQueue} disabled={!isDirty}>
          Lưu cấu hình
        </Button>
      </div>
    </div>
  );
}

export default BannerQueuePage;
